import { useState } from "react";
import { Link } from "react-router-dom";
import type { CategoryTreeNode } from "@/lib/api-client";

export interface MobileNavProps {
  /** Rayons de niveau 1 (déjà filtrés `isVisible`), avec leurs enfants directs. */
  categories: CategoryTreeNode[];
}

/**
 * Navigation mobile (VVS_MODELE_INTERFACE.md §1.4, variante mobile) : tiroir
 * plein écran à deux niveaux affichés en même temps. Porté à l'identique
 * depuis apps/web/components/layout/MobileNav.tsx (seul changement :
 * `next/link` → `react-router-dom` `Link`, `href` → `to`).
 */
export function MobileNav({ categories }: MobileNavProps) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="true"
        aria-expanded={open}
        className="inline-flex h-9 w-9 items-center justify-center text-xl text-cream lg:hidden"
      >
        <span aria-hidden="true">☰</span>
        <span className="sr-only">Ouvrir le menu des catégories</span>
      </button>

      {open ? (
        <div className="fixed inset-0 z-50 flex lg:hidden" role="dialog" aria-modal="true">
          <button type="button" aria-label="Fermer le menu" className="absolute inset-0 bg-navy/60" onClick={close} />
          <div className="relative flex h-full w-[85%] max-w-sm flex-col overflow-y-auto bg-cream p-4 shadow-xl">
            <div className="flex items-center justify-between border-b border-line pb-3">
              <span className="font-display text-lg font-bold text-navy">Catégories</span>
              <button type="button" onClick={close} className="text-xl text-navy" aria-label="Fermer le menu">
                ✕
              </button>
            </div>

            <ul className="mt-3 space-y-4 text-sm">
              {categories.map((category) => {
                const children = category.children.filter((child) => child.isVisible);
                return (
                  <li key={category.id}>
                    <Link
                      to={`/catalogue/${category.slug}`}
                      className="font-body text-sm font-semibold uppercase tracking-wide text-navy"
                      onClick={close}
                    >
                      {category.name}
                    </Link>
                    {children.length > 0 ? (
                      <ul className="mt-2 space-y-2 border-l border-line pl-3">
                        {children.map((child) => (
                          <li key={child.id}>
                            <Link
                              to={`/catalogue/${category.slug}/${child.slug}`}
                              className="font-body text-sm text-muted hover:text-gold"
                              onClick={close}
                            >
                              {child.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>

            <div className="mt-6 space-y-2 border-t border-line pt-4 text-sm">
              <Link to="/compte" className="block text-navy hover:text-gold" onClick={close}>
                Mon compte
              </Link>
              <Link to="/compte/commandes" className="block text-navy hover:text-gold" onClick={close}>
                Mes commandes
              </Link>
              <Link to="/compte/favoris" className="block text-navy hover:text-gold" onClick={close}>
                Mes favoris
              </Link>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
